import { useTranslation } from 'react-i18next';
import { Typography, useMediaQuery, useTheme } from '@mui/material';

import { PremiumDetailPillStyled } from '../PlanCard/PlanDetailsStyled';
import {
  ComparisonUnitStyled,
  ContentDetailsBoxStyled,
  ContentLabelStyled,
} from './ComparePlansStyled';
import { BenefitPlanDto } from 'enrollment/queries/useBenefitPlansQuery/types';

export interface ComparisonTableProps {
  planDetailsIds: string[];
  planContentDetails?: any;
  label: string;
  comparisonKeys?: string[];
  currentPlan?: BenefitPlanDto;
}

export const ComparisonTable = ({
  planDetailsIds,
  planContentDetails,
  label,
  comparisonKeys,
  currentPlan,
}: ComparisonTableProps) => {
  const { t } = useTranslation('enrollment.benefits');
  const theme = useTheme();
  const isScreenSmall = useMediaQuery(theme.breakpoints.down(1200));

  const displayDifferences = (key: string, index: number) => {
    return (
      <div className={`${isScreenSmall ? 'w-100' : 'equal-width'}`}>
        {isScreenSmall ? (
          <ContentLabelStyled fontSize={12} fontWeight="bold">
            {label}
          </ContentLabelStyled>
        ) : (
          <div />
        )}
        <ComparisonUnitStyled key={index}>
          {/* In Network */}
          {comparisonKeys?.includes('In-networks') && (
            <div className="prices">
              <Typography
                variant="body2"
                fontSize={`${isScreenSmall ? 14 : 12}`}
              >
                {planContentDetails[key][label]?.inNetwork
                  ? planContentDetails[key][label]?.inNetwork
                  : 'NA'}
              </Typography>
              <PremiumDetailPillStyled>
                <Typography fontSize={10}>{t('InNetworkCaps')}</Typography>
              </PremiumDetailPillStyled>
            </div>
          )}

          {/* Out of Network */}
          {comparisonKeys?.includes('Out-networks') && (
            <div className="prices">
              <Typography
                variant="body2"
                fontSize={`${isScreenSmall ? 14 : 12}`}
              >
                {planContentDetails[key][label]?.outNetwork
                  ? planContentDetails[key][label]?.outNetwork
                  : 'NA'}
              </Typography>
              <PremiumDetailPillStyled>
                <Typography fontSize={10}>{t('OutOfNetworkCaps')}</Typography>
              </PremiumDetailPillStyled>
            </div>
          )}

          {/* Preferred Network */}
          {comparisonKeys?.includes('Preferred-networks') && (
            <div className="prices">
              <Typography
                variant="body2"
                fontSize={`${isScreenSmall ? 14 : 12}`}
              >
                {planContentDetails[key][label]?.preferredNetwork
                  ? planContentDetails[key][label]?.preferredNetwork
                  : 'NA'}
              </Typography>
              <PremiumDetailPillStyled>
                <Typography fontSize={10}>
                  {t('PreferredNetworkCaps')}
                </Typography>
              </PremiumDetailPillStyled>
            </div>
          )}
        </ComparisonUnitStyled>
      </div>
    );
  };

  const displayContentDetails = () => {
    let updatedPlanContentDetails: any[] = [];
    planDetailsIds.map((planDetailsId: string) => {
      return Object.keys(planContentDetails)
        .filter((key) => key.toString() === planDetailsId.toString())
        .reduce((obj: { [key: string]: any }, key) => {
          obj[key] = planContentDetails[key];
          updatedPlanContentDetails = [...updatedPlanContentDetails, obj];
          return obj;
        }, {});
    });

    return updatedPlanContentDetails
      ?.map((content: object) => {
        return Object.keys(content)
          .map((key: string, index: number) => {
            return isScreenSmall
              ? key === currentPlan?.plan_id?.toString()
                ? displayDifferences(key, index)
                : null
              : displayDifferences(key, index);
          })
          .filter(Boolean); // This removes any `null` values from the array
      })
      .flat(); // This flattens the array of arrays into a single array
  };

  return (
    <ContentDetailsBoxStyled>
      {!isScreenSmall && (
        <ContentLabelStyled variant="body2" fontSize={12} fontWeight={600}>
          {label}
        </ContentLabelStyled>
      )}
      <div className="d-flex w-100 justify-content-between">
        {displayContentDetails()}
      </div>
    </ContentDetailsBoxStyled>
  );
};
